/* eslint-disable import/first */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button, Grid } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { useErrorHandler } from '../../../../Hooks/useErrorHandler';
import { useTheme } from '../../../../Components/Shared/GlobalContext/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useFileUploadPolicy } from '../../../../Hooks/useFileUploadPolicy';
import { ICON_SIZE, MOBILE_ICON_SIZE, commonIconButtonStyle } from '../../../Shared/Styles/iconButton';
import { shouldUseNativePicker } from '../../../../Utils/nativeFileUpload';
import { useVoiceRecording } from '../../../../Hooks/useVoiceRecording';
import { useFileUpload } from '../../../../Hooks/useFileUpload';
import { useEmojiPicker } from '../../../../Hooks/useEmojiPicker';
import { useMessageBeautify } from '../../../../Hooks/useMessageBeautify';

import FooterInput from './FooterInput/FooterInput';
import FooterUpload from './FooterUpload/FooterUpload';
import FooterVoice from './FooterVoice/FooterVoice';
import FooterPreview from './FooterPreview/FooterPreview';
import FooterEmoji from './FooterEmoji/FooterEmoji';

const MessageFooter = ({ onSendMessage, isSending, focusInputRef, conversationId, initialFileList = [] }) => {
    const [message, setMessage] = useState('');
    const textAreaRef = useRef(null);
    const { handleError } = useErrorHandler();
    const { isDarkMode } = useTheme();
    const { t, i18n } = useTranslation();
    const { policy, accept } = useFileUploadPolicy();
    const isNative = shouldUseNativePicker();
    const isRTL = (typeof i18n.dir === 'function' ? i18n.dir() === 'rtl' : false) || (i18n.language && i18n.language.startsWith('ar'));
    const screens = Grid.useBreakpoint();
    const isMobile = (!!screens.xs && !screens.sm) || false;

    // Mobile-optimized sizes
    const iconSize = isMobile ? MOBILE_ICON_SIZE : ICON_SIZE;
    const mobileButtonStyle = isMobile ? { ...commonIconButtonStyle, padding: '4px 4px', minWidth: '28px', height: '28px' } : commonIconButtonStyle;

    // Use custom hooks for feature logic
    const {
        fileList,
        setFileList,
        uploading,
        uploadingCamera,
        uploadingGallery,
        previewOpen,
        previewImage,
        handleFileUpload,
        handleBulkUpload,
        handleCameraClick,
        handleGalleryClick,
        handlePreview,
        handleChange,
        setPreviewOpen,
        setPreviewImage
    } = useFileUpload({ conversationId, initialFileList, policy, accept, isNative, t, handleError });

    const {
        isRecording,
        recordSeconds,
        pendingVoice,
        waveRef,
        wavePathRef,
        startRecording,
        pauseRecording,
        clearPendingVoice,
        sendPendingVoice,
        formatTime
    } = useVoiceRecording({ onSendMessage, handleFileUpload, handleError, t });

    const {
        emojiOpen,
        setEmojiOpen,
        handleEmojiSelect,
        handleEmojiButtonClick,
        ensureEmojiPreloaded,
        updateSelectionFromEvent
    } = useEmojiPicker({ message, setMessage, isMobile, textAreaRef });

    const {
        beautifying,
        runBeautifyMessage
    } = useMessageBeautify({ conversationId, t, handleError });

    const handleSendMessage = useCallback(() => {
        if (message.trim() || fileList.length > 0) {
            // Extract file IDs for attachments
            const attachment_ids = fileList
                .filter(file => file.fileId)
                .map(file => file.fileId);

            onSendMessage({
                message,
                attachment_ids
            });

            setMessage('');
            setFileList([]);
        }
    }, [message, fileList, onSendMessage, setFileList]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const hasText = !!(message && message.trim());
    const isSendDisabled = isSending || (!hasText && fileList.length === 0);

    return (
        <div
            onDragOver={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch (err) { } }}
            onDrop={(e) => {
                try {
                    e.preventDefault();
                    e.stopPropagation();
                    const files = Array.from(e?.dataTransfer?.files || []);
                    if (!files.length) return;
                    if (files.length > 1) {
                        handleBulkUpload(files);
                    } else {
                        const file = files[0];
                        (async () => {
                            const uploadedFile = await handleFileUpload(file);
                            if (uploadedFile) {
                                setFileList((prev) => [...prev, uploadedFile]);
                            }
                        })();
                    }
                } catch (err) { }
            }}
            style={{
                padding: '12px 16px',
                borderTop: `1px solid ${isDarkMode ? '#434343' : '#eff3f4'}`,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                marginBottom: 0,
                zIndex: 1
            }}
        >
            <FooterPreview
                fileList={fileList}
                setFileList={setFileList}
                handlePreview={handlePreview}
                handleChange={handleChange}
                handleFileUpload={handleFileUpload}
                accept={accept}
                uploading={uploading}
                previewOpen={previewOpen}
                previewImage={previewImage}
                setPreviewOpen={setPreviewOpen}
                setPreviewImage={setPreviewImage}
                pendingVoice={pendingVoice}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '12px' }}>
                {!pendingVoice && !isRecording && (
                    <FooterUpload
                        isNative={isNative}
                        accept={accept}
                        onFileUpload={handleFileUpload}
                        setFileList={setFileList}
                        uploading={uploading}
                        uploadingCamera={uploadingCamera}
                        uploadingGallery={uploadingGallery}
                        handleCameraClick={handleCameraClick}
                        handleGalleryClick={handleGalleryClick}
                        isMobile={isMobile}
                        mobileButtonStyle={mobileButtonStyle}
                        ICON_SIZE={ICON_SIZE}
                        iconSize={iconSize}
                    />
                )}

                <FooterInput
                    message={message}
                    setMessage={setMessage}
                    handleSendMessage={handleSendMessage}
                    handleKeyDown={handleKeyDown}
                    updateSelectionFromEvent={updateSelectionFromEvent}
                    isRecording={isRecording}
                    pendingVoice={pendingVoice}
                    textAreaRef={textAreaRef}
                    focusInputRef={focusInputRef}
                    isMobile={isMobile}
                    mobileButtonStyle={mobileButtonStyle}
                    beautifying={beautifying}
                    runBeautifyMessage={runBeautifyMessage}
                    iconSize={iconSize}
                />

                <FooterEmoji
                    isMobile={isMobile}
                    emojiOpen={emojiOpen}
                    setEmojiOpen={setEmojiOpen}
                    handleEmojiButtonClick={handleEmojiButtonClick}
                    ensureEmojiPreloaded={ensureEmojiPreloaded}
                    handleEmojiSelect={handleEmojiSelect}
                    pendingVoice={pendingVoice}
                    isRecording={isRecording}
                    isDarkMode={isDarkMode}
                    mobileButtonStyle={mobileButtonStyle}
                    ICON_SIZE={ICON_SIZE}
                    iconSize={iconSize}
                    renderPickerOnly={false}
                />

                <FooterVoice
                    isRecording={isRecording}
                    pendingVoice={pendingVoice}
                    recordSeconds={recordSeconds}
                    waveRef={waveRef}
                    wavePathRef={wavePathRef}
                    startRecording={startRecording}
                    pauseRecording={pauseRecording}
                    clearPendingVoice={clearPendingVoice}
                    sendPendingVoice={sendPendingVoice}
                    formatTime={formatTime}
                    setEmojiOpen={setEmojiOpen}
                    hasText={hasText}
                    ICON_SIZE={ICON_SIZE}
                />

                {!pendingVoice && !isRecording && (
                    <Button
                        type="primary"
                        icon={<SendOutlined style={{ transform: isRTL ? 'scaleX(-1)' : undefined }} />}
                        onClick={handleSendMessage}
                        loading={isSending}
                        disabled={isSendDisabled}
                        style={{
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isSendDisabled ? (isDarkMode ? '#434343' : '#bfbfbf') : '#1DA1F2',
                            border: 'none',
                            flexShrink: 0
                        }}
                    />
                )}
            </div>

            {isMobile && (
                <FooterEmoji
                    isMobile={isMobile}
                    emojiOpen={emojiOpen}
                    setEmojiOpen={setEmojiOpen}
                    handleEmojiButtonClick={handleEmojiButtonClick}
                    ensureEmojiPreloaded={ensureEmojiPreloaded}
                    handleEmojiSelect={handleEmojiSelect}
                    pendingVoice={pendingVoice}
                    isRecording={isRecording}
                    isDarkMode={isDarkMode}
                    mobileButtonStyle={mobileButtonStyle}
                    ICON_SIZE={ICON_SIZE}
                    iconSize={iconSize}
                    renderPickerOnly={true}
                />
            )}
        </div>
    );
};
export default MessageFooter;